import React, { useEffect, useRef, useState } from 'react';
import './lease-form.scss';

import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Autocomplete,
  Link,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const formatList = (items) => {
  if (!items?.length) return 'None';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const HighlightedText = ({ children }) => (
  <span style={{ color: 'red', fontWeight: 'bold' }}>{children}</span>
);

const Step3 = ({ formik, isEditMode = false }) => {
  const appliancesString = formatList(formik.values?.property_details?.appliances || []);
  return (
    <Box className="lease-form-step3">
      {/* Section 8: Appliances */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">8. APPLIANCES</Typography>
        <Typography variant="body2" className="section-content">
          The Tenant agrees to keep clean and in good condition all assets, including appliances,
          furnishings, and equipment contained at the time of possession in the Premises, which
          specifically includes, without limitation:<HighlightedText>{appliancesString}</HighlightedText>.
        </Typography>
      </Box>

      {/* Section 9 */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title"> 9. OTHER OCCUPANTS</Typography>
        <Typography variant="body2" className="section-content">
          The tenant agrees that in addition to the tenant, the premises may be occupied only by the following persons:
          <br />
          <OccupantsForm formik={formik} isEditMode={isEditMode} />
          Together with any natural increase in the tenant’s family, but not to exceed a total of 2 Adult unless the landlord consents in
          writing to the occupation of the premises by some other or additional person or persons. Each approved additional individual
          occupant will be charged $50.00 extra per month.
        </Typography>
      </Box>

      {/* Section 10 */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">10. CONDITION REPORT</Typography>
        <Typography variant="body2" className="section-content">
          Prior to the tenant taking possession of the premises, the landlord and the tenant shall complete and execute an entry condition report detailing the condition of the premises, fixtures and appliances, and the said report shall be conclusive evidence of the condition of the premises, fixtures, and appliances, an exit report shall be similarly completed and executed and all charges for repair of damages and cleaning of the premises will be based on these reports. When Tenant is moving out- they are responsible to get all carpets steam cleaned and provide Management with a proof of receipt.
        </Typography>
      </Box>

      {/* Section 11 */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">
          11. TENANT INSURANCE
        </Typography>

        <Typography
          variant="body2"
          className="section-content"
          sx={{
            backgroundColor: '#ffff99',
            color: 'black',
            p: 1,
            borderRadius: 1,
            fontWeight: 'bold'
          }}
        >
          The Tenant promises to hold a valid insurance policy, which includes the Tenant’s contents in the Home,
          including contents in vehicle. The policy is required to insure the Tenants and any guests against loss or damages,
          including personal injury and death. The Tenant promises to carry general comprehensive liability insurance to a
          minimum of $2 Million Liability coverage. Proof of current and accurate Tenant’s Insurance is required before
          move-in. Tenant Insurance is mandatory.
        </Typography>
      </Box>

      {/* Section 12 */}
      <Box className="section">
        THE TENANT HEREBY COVENANTS WITH THE LANDLORD AS FOLLOWS:
        <br />
        <br />
        <Typography variant="subtitle2" className="section-title">12. SAFETY & REPAIRS</Typography>
        <Typography variant="body2" className="section-content">
          The tenant will not do anything, nor neglect to do anything by which fire or health hazard is created. The tenant will comply with all fire, health, and sanitation regulations. The tenant shall take good care of the premises and keep the same in reasonably clean condition. The tenant will be responsible for repairing and replacing windows, screens, light bulbs, fuses, fixtures, appliances and all floor coverings and counter tops damaged, broken, removed, destroyed, stained, or soiled by the tenant, or the tenant's guests or invitees. The tenant will be responsible for and promptly pay on demand all charges relating to any service supplied to or used on the premises where properly chargeable to the tenant through causation or neglect including maintenance of yards and sidewalks, plugged toilets, sinks and drains etc. The cost of any damage resulting directly or indirectly there from. Charges will be levied at $40 per hour plus travel time and equipment use if done by the landlord's own forces, or at the charge levied by people he may hire plus 20% to cover overseeing. The tenant shall be responsible for all damages resulting from the escape of water within or from the leased premises, and from the use of defective electrical or other appliances of any kind. Leaks in pipes, are to be repaired by the landlord within a reasonable time after notice, but the landlord shall not be liable or responsible for any damages resulting from such leaks or overflow.
        </Typography>
      </Box>

      {/* Section 13 */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">13. ASSIGNMENT & GUESTS</Typography>
        <Typography variant="body2" className="section-content">
          During the term of this lease, if the tenant wants to sublet, assign or re-let the Leased Premises, the permission
          needs to be obtained from the landlord. (This Includes Parking stalls/Storages). The tenant will not leave guests
          in charge of the leased premises without first obtaining the written consent of the landlord, such consent will be
          arbitrarily withheld, nor allow guests to reside in the premises for a period greater than 1 week, (7 Days) without
          written consent from the landlord.
        </Typography>
      </Box>
    </Box >
  );
}

const OccupantsForm = ({ formik, isEditMode = false }) => {
  const [occupants, setOccupants] = useState(formik?.values?.occupants || [{ name: '', relationship: '', age: '' }]);
  const [editIndex, setEditIndex] = useState(null);
  const [editField, setEditField] = useState(null);
  const [isNA, setIsNA] = useState(false);
  const [ageInput, setAgeInput] = useState(
    occupants.map((occupant) => ({
      years: occupant.age ? Math.floor(occupant.age / 12) : '',
      months: occupant.age ? occupant.age % 12 : '',
    }))
  );

  const inputRefs = useRef([]);

  const relationshipOptions = [
    'Friend',
    'Spouse',
    'Child',
    'Parent',
    'Sibling',
    'Other',
  ];

  const convertToYearsAndMonths = (months) => {
    if (!months && months !== 0)
      return <span style={{ color: '#aaa' }}>Click to edit</span>;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    let displayText = '';
    if (years > 0) displayText += `${years} year${years !== 1 ? 's' : ''}`;
    if (years > 0 && remainingMonths > 0) displayText += ' and ';
    if (remainingMonths > 0)
      displayText += `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    return displayText || '0 months';
  };

  const handleInputChange = (index, field, value) => {
    const updatedOccupants = [...occupants];
    if (field === 'age') {
      updatedOccupants[index] = {
        ...updatedOccupants[index],
        [field]: Number(value),
      };
    } else {
      updatedOccupants[index] = {
        ...updatedOccupants[index],
        [field]: value,
      };
    }
    setOccupants(updatedOccupants);
    setTimeout(() => {
      if (formik && formik.setFieldValue) {
        formik.setFieldValue('occupants', updatedOccupants);
      }
    }, 0);
  };

  const handleAgeInputChange = (index, field, value) => {
    const numValue = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    const finalValue = field === 'months' && numValue > 11 ? 11 : numValue;

    const updatedAgeInput = [...ageInput];
    const current = updatedAgeInput[index] || { years: '', months: '' };

    const newAge = {
      ...current,
      [field]: finalValue,
    };

    updatedAgeInput[index] = newAge;
    setAgeInput(updatedAgeInput);

    const totalMonths = (parseInt(newAge.years) || 0) * 12 + (parseInt(newAge.months) || 0);

    handleInputChange(index, 'age', totalMonths);
  };

  const handleAgeBlur = (index) => {
    setEditIndex(null);
    setEditField(null);
  };

  const handleBlur = (index, field, event) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      if (field === 'name') {
        setEditField('relationship');
        setTimeout(() => {
          const relationshipInput = inputRefs.current[`${index}-relationship`];
          if (relationshipInput) relationshipInput.focus();
        }, 0);
      } else if (field === 'relationship') {
        setEditField('age');
        setTimeout(() => {
          const ageInput = inputRefs.current[`${index}-age-years`];
          if (ageInput) ageInput.focus();
        }, 0);
      }
    } else if (event.type === 'blur') {
      setEditIndex(null);
      setEditField(null);
    }
  };

  const handleCellClick = (index, field) => {
    if (!isNA) {
      setEditIndex(index);
      setEditField(field);
      setTimeout(() => {
        const input = inputRefs.current[`${index}-${field}`];
        if (input) input.focus();
      }, 0);
    }
  };

  const handleAddOccupant = () => {
    if (isNA) return;

    const newOccupant = { name: '', relationship: '', age: '' };
    const updatedOccupants = [...occupants, newOccupant];
    setOccupants(updatedOccupants);
    setAgeInput([...ageInput, { years: '', months: '' }]);

    setTimeout(() => {
      if (formik && formik.setFieldValue) {
        formik.setFieldValue('occupants', updatedOccupants);
      }
    }, 0);
  };

  const handleDelete = (index) => {
    if (isNA) return;

    const updatedOccupants = occupants.filter((_, i) => i !== index);
    const updatedAgeInput = ageInput.filter((_, i) => i !== index);
    setOccupants(updatedOccupants);
    setAgeInput(updatedAgeInput);

    setTimeout(() => {
      if (formik && formik.setFieldValue) {
        formik.setFieldValue('occupants', updatedOccupants);
      }
    }, 0);
  };

  const handleNAChange = (checked) => {
    setIsNA(checked);
    if (checked) {
      setOccupants([]);
      setAgeInput([]);
      formik.setFieldValue('occupants', []);
    } else {
      const defaultOccupant = [{ name: '', relationship: '', age: '' }];
      setOccupants(defaultOccupant);
      setAgeInput([{ years: '', months: '' }]);
      formik.setFieldValue('occupants', defaultOccupant);
    }
  };

  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return; // Prevent running again

    if (isEditMode && formik?.values?.occupants?.length === 0) {
      // Edit mode with empty occupants: set N/A to true
      setIsNA(true);
      setOccupants([]);
      setAgeInput([]);
      formik.setFieldValue('occupants', []);
    } else if (formik?.values?.occupants && formik.values.occupants.length > 0) {
      // Non-empty occupants: use provided data
      setOccupants(formik.values.occupants);
      setAgeInput(
        formik.values.occupants.map((occupant) => ({
          years: occupant.age ? Math.floor(occupant.age / 12) : '',
          months: occupant.age ? occupant.age % 12 : '',
        }))
      );
      setIsNA(false);
    } else {
      // Default case: initialize with one empty occupant
      const defaultOccupant = [{ name: '', relationship: '', age: '' }];
      setOccupants(defaultOccupant);
      setAgeInput([{ years: '', months: '' }]);
      setIsNA(false);
      formik.setFieldValue('occupants', defaultOccupant);
    }

    initializedRef.current = true;
  }, [isEditMode]);

  return (
    <Box className="occupants-details-table" sx={{ my: 2 }}>
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Relationship</TableCell>
              <TableCell>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  Age
                  <div className="form-check form-switch" dir="ltr">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="occupantsNASwitch"
                      checked={isNA}
                      onChange={(e) => handleNAChange(e.target.checked)}
                    />
                    <label className="form-check-label ms-1" htmlFor="occupantsNASwitch">
                      N/A
                    </label>
                  </div>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isNA ? (
              <TableRow>
                <TableCell align="center" sx={{ color: '#666', fontStyle: 'italic' }}>
                  N/A
                </TableCell>
                <TableCell align="center" sx={{ color: '#666', fontStyle: 'italic' }}>
                  N/A
                </TableCell>
                <TableCell align="center" sx={{ color: '#666', fontStyle: 'italic' }}>
                  N/A
                </TableCell>
              </TableRow>
            ) : (
              occupants.map((occupant, index) => (
                <TableRow key={index}>
                  <TableCell onClick={() => handleCellClick(index, 'name')}>
                    {editIndex === index && editField === 'name' ? (
                      <TextField
                        fullWidth
                        autoFocus
                        variant="standard"
                        value={occupant.name || ''}
                        onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                        onBlur={(e) => handleBlur(index, 'name', e)}
                        onKeyDown={(e) => handleBlur(index, 'name', e)}
                        size="small"
                        placeholder="Enter name"
                        inputRef={(el) => (inputRefs.current[`${index}-name`] = el)}
                      />
                    ) : (
                      occupant.name || <span style={{ color: '#aaa' }}>Click to edit</span>
                    )}
                  </TableCell>

                  <TableCell onClick={() => handleCellClick(index, 'relationship')}>
                    {editIndex === index && editField === 'relationship' ? (
                      <Autocomplete
                        freeSolo
                        autoSelect
                        options={relationshipOptions}
                        value={occupant.relationship || ''}
                        onChange={(_, newValue) =>
                          handleInputChange(index, 'relationship', newValue || '')
                        }
                        onInputChange={(_, newInputValue) =>
                          handleInputChange(index, 'relationship', newInputValue)
                        }
                        onBlur={(e) => handleBlur(index, 'relationship', e)}
                        onKeyDown={(e) => handleBlur(index, 'relationship', e)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            autoFocus
                            placeholder="Enter or select"
                            size="small"
                            inputRef={(el) => (inputRefs.current[`${index}-relationship`] = el)}
                          />
                        )}
                      />
                    ) : (
                      occupant.relationship || <span style={{ color: '#aaa' }}>Click to edit</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        onClick={() => handleCellClick(index, 'age')}
                        sx={{ flexGrow: 1, cursor: 'pointer' }}
                      >
                        {editIndex === index && editField === 'age' ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              variant="standard"
                              value={ageInput[index]?.years || ''}
                              onChange={(e) => handleAgeInputChange(index, 'years', e.target.value)}
                              onBlur={() => handleAgeBlur(index)}
                              onKeyDown={(e) => {
                                if (e.key === 'Tab' && !e.shiftKey) {
                                  e.preventDefault();
                                  const monthsInput = inputRefs.current[`${index}-age-months`];
                                  if (monthsInput) monthsInput.focus();
                                }
                              }}
                              size="small"
                              placeholder="Years"
                              type="number"
                              inputProps={{ min: 0, max: 120 }}
                              sx={{ width: '70px' }}
                              inputRef={(el) => (inputRefs.current[`${index}-age-years`] = el)}
                            />
                            <TextField
                              variant="standard"
                              value={ageInput[index]?.months || ''}
                              onChange={(e) => handleAgeInputChange(index, 'months', e.target.value)}
                              onBlur={() => handleAgeBlur(index)}
                              onKeyDown={(e) => handleBlur(index, 'age', e)}
                              size="small"
                              placeholder="Months"
                              type="number"
                              inputProps={{ min: 0, max: 11 }}
                              sx={{ width: '70px' }}
                              inputRef={(el) => (inputRefs.current[`${index}-age-months`] = el)}
                            />
                          </Box>
                        ) : (
                          convertToYearsAndMonths(occupant.age)
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(index)}
                        disabled={isNA}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>

                      {index === occupants.length - 1 && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleAddOccupant}
                          sx={{ ml: 1 }}
                          disabled={isNA}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export { Step3 }